import Image from "next/image";

// next/image requires either a static-imported source or a string URL that
// resolves into one of the `images.remotePatterns` allow-listed hosts. CMS
// featured images come back as absolute URLs against the admin origin
// (already allow-listed) or as server-relative paths under /img/... (works
// as-is). Anything else (broken, empty, or an unsupported scheme) falls
// through to a plain <img> so the markup never silently disappears.
//
// In every branch we forward className/style so callers can keep their
// layout rules, and we always emit explicit width/height (or `fill` mode)
// so Lighthouse stops flagging the implicit-dimensions warning.
const SafeImage = ({
  src,
  alt,
  width,
  height,
  fill = false,
  sizes,
  quality = 75,
  priority = false,
  loading,
  className,
  style,
  onError,
  ...rest
}) => {
  if (!src) return null;

  const isAbsolute = /^https?:\/\//i.test(src);
  const isRelative = src.startsWith("/");
  const canOptimize = isAbsolute || isRelative;

  if (!canOptimize) {
    // Bare filename like "1.webp" — let the caller resolve via
    // resolveProductImage() before passing in. If we still get one,
    // render raw to avoid a 404 from the optimizer.
    return (
      <img
        src={src}
        alt={alt}
        width={width}
        height={height}
        loading={loading}
        className={className}
        style={style}
        onError={onError}
        decoding="async"
        {...rest}
      />
    );
  }

  if (fill) {
    return (
      <Image
        src={src}
        alt={alt}
        fill
        sizes={sizes}
        quality={quality}
        priority={priority}
        loading={loading}
        className={className}
        style={style}
        onError={onError}
        {...rest}
      />
    );
  }

  return (
    <Image
      src={src}
      alt={alt}
      width={width}
      height={height}
      sizes={sizes}
      quality={quality}
      priority={priority}
      loading={loading}
      className={className}
      style={style}
      onError={onError}
      {...rest}
    />
  );
};

export default SafeImage;
